import React, { useState } from "react";
import { Limit, Plan } from "../../../../Entities/Plan";
import { User } from "../../../../Entities/User";
import { Subscription } from "../../../../Entities/Subscription";
import { Button, Descriptions, DescriptionsProps, Typography } from "antd";
import { formatPlanName } from "../../../../Utils/planUtils";
import { getRemainingDays } from "../../../../Utils/generic-functions";
import dayjs from "dayjs";
import { CurrentAccessAddon } from "../../../../Hooks/useAccess";
import useCountUp from "../../../../Utils/countUpAnimation";
import CreditUsageDrawer from "./CreditUsageDrawer";
import { EyeOutlined } from "@ant-design/icons";

interface PlanDetailsProps {
  plan: Plan;
  subscription: Subscription;
  addons: CurrentAccessAddon[];
  limits: Limit;
  user: User | null;
  subscriptionMode: "base" | "credit" | "free";
}

const PlanDetails: React.FC<PlanDetailsProps> = ({
  plan,
  subscription,
  addons,
  limits,
  user,
  subscriptionMode,
}) => {
  const [isCreditUsageDrawerVisible, setIsCreditUsageDrawerVisible] =
    useState(false);
  const isAnnualMode =
    subscription?.startDate &&
    subscription?.endDate &&
    dayjs(subscription.endDate).diff(dayjs(subscription.startDate), "month") >
      1;

  const getPlanTypeLabel = () => {
    if (subscription?.mode === "credit") return "Credit Based";
    if (subscriptionMode === "free") return "Free";
    return "Subscription Based";
  };

  const items: DescriptionsProps["items"] = [
    {
      label: <Typography.Text strong>Plan</Typography.Text>,
      span: "filled",
      children:
        subscriptionMode === "credit"
          ? "QualifyMe Credit Plan"
          : formatPlanName(plan?.name),
    },
    {
      label: <Typography.Text strong>Plan Type</Typography.Text>,
      span: "filled",
      children: getPlanTypeLabel(),
    },
  ];

  const jobApplicationAddon = addons.find(
    (addon: CurrentAccessAddon) => addon.type === "job-application"
  );
  const jobPostAddon = addons.find(
    (addon: CurrentAccessAddon) => addon.type === "job-post"
  );

  if (subscription?.mode !== "credit") {
    items.push(
      {
        label: <Typography.Text strong>Job Post Addons</Typography.Text>,
        span: "filled",
        children:
          jobPostAddon && jobPostAddon.quantity > 0
            ? `X ${jobPostAddon.quantity}`
            : "None",
      },
      {
        label: <Typography.Text strong>Assessment Addons</Typography.Text>,
        span: "filled",
        children:
          jobApplicationAddon && jobApplicationAddon.quantity > 0
            ? `X ${jobApplicationAddon.quantity}`
            : "None",
      }
    );
  }

  if (subscription?.mode === "base") {
    items.push({
      label: <Typography.Text strong>Billing Period</Typography.Text>,
      span: "filled",
      children: isAnnualMode ? "Annual" : "Monthly",
    });
  }

  items.push(
    {
      label: <Typography.Text strong>Start Date</Typography.Text>,
      span: "filled",
      children: dayjs(subscription?.startDate).format("DD MMM YYYY"),
    },
    {
      label: <Typography.Text strong>Expiry</Typography.Text>,
      span: "filled",
      children:
        getRemainingDays(subscription?.endDate) >= 0 ? (
          `${getRemainingDays(subscription?.endDate)} days left`
        ) : (
          <Typography.Text type="danger">Expired</Typography.Text>
        ),
    }
  );

  const getCreditBalance = () => {
    const purchasedCredits = limits.applicationsProcessed || 0;
    const usedCredits = subscription?.measurements?.applicationsProcessed || 0;
    return purchasedCredits - usedCredits;
  };

  const { count: creditBalance } = useCountUp(getCreditBalance());

  const handleViewDetailedUsage = () => {
    setIsCreditUsageDrawerVisible(true);
  };

  return (
    <section className="plan-details-container">
      {subscriptionMode === "credit" && (
        <div className="credit-based-subscription-details-container">
          <Typography.Title level={3} className="credit-balance-value">
            {creditBalance}
          </Typography.Title>
          <Typography.Text
            type="secondary"
            strong
            className="credit-balance-label"
          >
            Credit Balance
          </Typography.Text>
          <div className="credit-balance-links">
            <Button
              type="link"
              onClick={handleViewDetailedUsage}
              icon={<EyeOutlined />}
            >
              View Detailed Usage
            </Button>
          </div>
        </div>
      )}
      <Descriptions bordered items={items} />

      <CreditUsageDrawer
        isVisible={isCreditUsageDrawerVisible}
        setIsVisible={setIsCreditUsageDrawerVisible}
      />
    </section>
  );
};

export default PlanDetails;
